import { ARROW } from "../../common/utility"
import { passSetGamepads } from "../main2worker"
import { getArrowKeysAsJoystick } from "../ui_settings"
import { CustomGamepad } from "./customgamepad"

// Keep these outside so we can have both X and Y axes set at the same time.
const arrowGamePad = [0, 0]

// Cache the last gamepad state to avoid sending duplicate updates
let lastGamepadState: EmuGamepad[] = []

const AXIS_THRESHOLD = 0.01 // Ignore changes smaller than this

const gamepadStateChanged = (current: EmuGamepad[], previous: EmuGamepad[]): boolean => {
  if (current.length !== previous.length) return true
  
  for (let i = 0; i < current.length; i++) {
    const curr = current[i]
    const prev = previous[i]
    
    // Check axes with threshold
    if (curr.axes.length !== prev.axes.length) return true
    for (let j = 0; j < curr.axes.length; j++) {
      if (Math.abs(curr.axes[j] - prev.axes[j]) > AXIS_THRESHOLD) return true
    }
    
    // Check buttons
    if (curr.buttons.length !== prev.buttons.length) return true
    for (let j = 0; j < curr.buttons.length; j++) {
      if (curr.buttons[j] !== prev.buttons[j]) return true
    }
  }
  
  return false
}

const convertArrowGamepadToValue = (index: number) => {
  const indexToValue = [0, 0.25, 0.5, 0.75, 1, 0]
  if (index < 0) {
    return -indexToValue[-index]
  } else {
    return indexToValue[index]
  }
}

const checkArrowKeyGamepadValues = () => {
  const gamePad: EmuGamepad[] = [{
    axes: [convertArrowGamepadToValue(arrowGamePad[0]),
      convertArrowGamepadToValue(arrowGamePad[1]), 0, 0],
    buttons: []
  }]
  passSetGamepads(gamePad)
  if (arrowGamePad[0] < 0 && arrowGamePad[0] > -4) {
    arrowGamePad[0]--
  } else if (arrowGamePad[0] > 0 && arrowGamePad[0] < 4) {
    arrowGamePad[0]++
  }
  if (arrowGamePad[1] < 0 && arrowGamePad[1] > -4) {
    arrowGamePad[1]--
  } else if (arrowGamePad[1] > 0 && arrowGamePad[1] < 4) {
    arrowGamePad[1]++
  }
  if (Math.abs(arrowGamePad[0]) === 5) {
    arrowGamePad[0] = 0
  }
  if (Math.abs(arrowGamePad[1]) === 5) {
    arrowGamePad[1] = 0
  }
}

let customGamepad: (CustomGamepad | null) = null

// Force browser to update gamepad state by listening to events,
// even if we don't really do anything with the events.
// Otherwise the browser will sometimes not update the state.
let gamepadEventListenersAdded = false
export const ensureGamepadEventListeners = () => {
  if (!gamepadEventListenersAdded) {
    gamepadEventListenersAdded = true
    window.addEventListener("gamepadconnected", () => {
      console.log("Gamepad connected")
    })
    window.addEventListener("gamepaddisconnected", () => {
      console.log("Gamepad disconnected")
    })
  }
}

export const setCustomGamepad = (buttons: boolean[] | null, axes: number[] | null) => {
  if (!customGamepad) {
    customGamepad = new CustomGamepad()
  }
  if (buttons) {
    customGamepad.setButtons(buttons)
  }
  if (axes) {
    customGamepad.setAxes(axes)
  }
}

export const clearCustomGamepad = () => {
  customGamepad = null
}

const getGamepads = () => {
  if (customGamepad) {
    return [customGamepad]
  }
  const gamepads = navigator.getGamepads().filter((gp) => (gp !== null))
  return gamepads
}

// Some controllers report the D-pad as special values for axes[9].
// Convert those to standard axes[0] and axes[1] values.
const checkXboxDpad = (axes: number[]) => {
  if (axes.length >= 10) {
    if ((Math.abs(axes[9] - 0.71)) < AXIS_THRESHOLD) {
      axes[0] = -1
    } else if ((Math.abs(axes[9] - 0.14)) < AXIS_THRESHOLD) {
      axes[1] = 1
    } else if ((Math.abs(axes[9] - (-1.0))) < AXIS_THRESHOLD) {
      axes[1] = -1
    } else if ((Math.abs(axes[9] - (-0.43))) < AXIS_THRESHOLD) {
      axes[0] = 1
    }
  }
}

export const checkGamepad = () => {
  const gamepads = getGamepads()
  // If we don't have a gamepad, see if we're using our arrow keys as a joystick
  if (!gamepads || gamepads.length < 1) {
    if (getArrowKeysAsJoystick() && arrowGamePad[0] !== 0 || arrowGamePad[1] !== 0) {
      checkArrowKeyGamepadValues()
    }  
    return
  }
  const gamePad: EmuGamepad[] = []
  for (let i = 0; i < gamepads.length; i++) {
    const buttons = gamepads[i]?.buttons
    if (gamepads[i]?.axes && buttons) {
      const axes = (gamepads[i].axes).slice()
      if (axes.length >= 10) {
        checkXboxDpad(axes)
      }
      gamePad.push({axes: axes, buttons: buttons.map(b => b.pressed)})
    }
  }
  if (gamePad.length > 0) {
    // Only send update if state has changed
    if (gamepadStateChanged(gamePad, lastGamepadState)) {
      lastGamepadState = gamePad
      passSetGamepads(gamePad)
    }
  }
}

let withinRumble = false

export const doRumble = (params: GamePadActuatorEffect) => {
  const gamepads = getGamepads()
  if (!gamepads || gamepads.length < 1) return
  if (withinRumble) return
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gp = gamepads[0] as any
  if (gp && "vibrationActuator" in gp && gp.vibrationActuator) {
    gp.vibrationActuator.playEffect("dual-rumble", params)
    withinRumble = true
    setTimeout(() => {
      withinRumble = false
    }
    , params.duration)
  }
}

export const handleArrowKey = (key: ARROW, release: boolean) => {
  if (!release) {
    if (getArrowKeysAsJoystick()) {
      switch (key) {
        case ARROW.LEFT:
          arrowGamePad[0] = -1
          break
        case ARROW.RIGHT:
          arrowGamePad[0] = 1
          break
        case ARROW.UP:
          arrowGamePad[1] = -1
          break
        case ARROW.DOWN:
          arrowGamePad[1] = 1
          break
      }
      checkArrowKeyGamepadValues()
    }
  } else {
    switch (key) {
      case ARROW.LEFT:
        if (arrowGamePad[0] < 0) {
          arrowGamePad[0] = 0
        }
        break
      case ARROW.RIGHT:
        if (arrowGamePad[0] > 0) {
          arrowGamePad[0] = 0
        }
        break
      case ARROW.UP:
        if (arrowGamePad[1] < 0) {
          arrowGamePad[1] = 0
        }
        break
      case ARROW.DOWN:
        if (arrowGamePad[1] > 0) {
          arrowGamePad[1] = 0
        }
        break
    }
    checkArrowKeyGamepadValues()
  }
}

export const handleDiagonalKey = (code: string, release: boolean) => {
  if (!getArrowKeysAsJoystick()) return
  if (!release) {
    switch (code) {
      case "Numpad7":
        arrowGamePad[0] = -1
        arrowGamePad[1] = -1
        break
      case "Numpad9":
        arrowGamePad[0] = 1
        arrowGamePad[1] = -1
        break
      case "Numpad1":
        arrowGamePad[0] = -1
        arrowGamePad[1] = 1
        break
      case "Numpad3":
        arrowGamePad[0] = 1
        arrowGamePad[1] = 1
        break
    }
  } else {
    switch (code) {
      case "Numpad7":
        if (arrowGamePad[0] < 0) arrowGamePad[0] = 0
        if (arrowGamePad[1] < 0) arrowGamePad[1] = 0
        break
      case "Numpad9":
        if (arrowGamePad[0] > 0) arrowGamePad[0] = 0
        if (arrowGamePad[1] < 0) arrowGamePad[1] = 0
        break
      case "Numpad1":
        if (arrowGamePad[0] < 0) arrowGamePad[0] = 0
        if (arrowGamePad[1] > 0) arrowGamePad[1] = 0
        break
      case "Numpad3":
        if (arrowGamePad[0] > 0) arrowGamePad[0] = 0
        if (arrowGamePad[1] > 0) arrowGamePad[1] = 0
        break
    }
  }
  checkArrowKeyGamepadValues()
}
